<script setup>
import { ref, onMounted } from 'vue';
import { globalState, getUserAsync } from '@/lib/user';
import { useToast } from '@/components/ui/toast/use-toast';
import { request } from '@/lib/api';
import { i18n } from '@/i18n';

const { toast } = useToast();

const showEmailVerificationBanner = ref(false);
const isResendingEmail = ref(false);
const showVerificationInput = ref(false);
const verificationCode = ref('');
const isVerifying = ref(false);

onMounted(() => {
  getUserAsync().then((user) => {
    if (user && user.email && !user.email_verified) {
      showEmailVerificationBanner.value = true;
    }
  });
});

async function resendVerificationEmail() {
  if (isResendingEmail.value) return;
  isResendingEmail.value = true;

  try {
    const language = i18n.language;
    const res = await request(`/api/send-verification-email?language=${language}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    if (res.verified) {
      getUserAsync().then(() => {
        showEmailVerificationBanner.value = false;
      });
      return;
    }
    toast({
      title: i18n.get('auth.verificationEmailSent'),
      description: i18n.get('auth.checkInbox'),
      variant: 'success',
    });
  } catch (error) {
    toast({
      title: i18n.get('auth.verificationEmailFailed'),
      description: `${i18n.get('auth.tryAgainLater')} ${error.message}`,
      variant: 'destructive',
    });
  } finally {
    isResendingEmail.value = false;
  }
}

async function verifyCode() {
  if (isVerifying.value || !verificationCode.value.trim()) return;
  isVerifying.value = true;

  try {
    const language = i18n.language;
    const res = await request(`/api/user/verify`, {
      method: 'POST',
      body: JSON.stringify({
        code: verificationCode.value.trim(),
        language: language,
      }),
    });

    if (res.success) {
      getUserAsync().then(() => {
        showEmailVerificationBanner.value = false;
      });
      toast({
        title: i18n.get('auth.verificationSuccess'),
        description: i18n.get('auth.emailVerified'),
        variant: 'success',
      });
    } else {
      toast({
        title: i18n.get('auth.verificationFailed'),
        description: res.message || i18n.get('auth.invalidCode'),
        variant: 'destructive',
      });
    }
  } catch (error) {
    toast({
      title: i18n.get('auth.verificationFailed'),
      description: `${i18n.get('auth.tryAgainLater')} ${error.message}`,
      variant: 'destructive',
    });
  } finally {
    isVerifying.value = false;
  }
}

function toggleVerificationInput() {
  showVerificationInput.value = !showVerificationInput.value;
  if (showVerificationInput.value) {
    verificationCode.value = '';
  }
}
</script>

<template>
  <div
    v-if="showEmailVerificationBanner"
    class="bg-gradient-to-r from-blue-600 to-indigo-700 text-white py-3 px-4 shadow transition-all duration-300"
  >
    <div class="container mx-auto flex flex-col sm:flex-row items-center justify-between">
      <div class="flex items-center mb-3 sm:mb-0">
        <div class="flex-shrink-0 mr-3">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="h-6 w-6"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
            />
          </svg>
        </div>
        <div>
          <h3 class="font-bold text-sm sm:text-base">
            {{ $t('auth.emailVerificationRequired') }}
          </h3>
          <p class="text-xs sm:text-sm text-white/90">
            {{ $t('auth.verificationDescription') }}
            <span class="font-medium">{{ globalState.user.email }}</span>
          </p>
        </div>
      </div>
      <div class="flex flex-col sm:flex-row items-center gap-2">
        <button
          @click="toggleVerificationInput"
          class="px-3 py-2 bg-white/10 text-white rounded text-sm font-medium hover:bg-white/20 active:bg-white/30 transition-colors duration-200 border border-white/20"
        >
          {{ showVerificationInput ? $t('auth.cancel') : $t('auth.enterCode') }}
        </button>
        <button
          @click="resendVerificationEmail"
          class="px-4 py-2 bg-white text-indigo-700 rounded text-sm font-medium hover:bg-white/90 active:bg-white/80 transition-colors duration-200"
          :disabled="isResendingEmail"
        >
          <span v-if="isResendingEmail">{{ $t('auth.sending') }}</span>
          <span v-else>{{ $t('auth.resendVerificationEmail') }}</span>
        </button>
      </div>
    </div>

    <!-- Verification Code Input Section -->
    <div v-if="showVerificationInput" class="container mx-auto mt-4 pt-4 border-t border-white/20">
      <div class="flex flex-col sm:flex-row items-center gap-3">
        <div class="flex-1 w-full sm:w-auto">
          <input
            v-model="verificationCode"
            type="text"
            placeholder="Enter verification code"
            class="w-full px-3 py-2 bg-white/10 text-white placeholder-white/60 rounded border border-white/20 focus:border-white/40 focus:outline-none focus:ring-2 focus:ring-white/20 text-sm"
            :disabled="isVerifying"
            @keyup.enter="verifyCode"
          />
        </div>
        <button
          @click="verifyCode"
          class="px-4 py-2 bg-green-500 text-white rounded text-sm font-medium hover:bg-green-600 active:bg-green-700 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          :disabled="isVerifying || !verificationCode.trim()"
        >
          <span v-if="isVerifying">{{ $t('auth.verifying') }}</span>
          <span v-else>{{ $t('auth.verify') }}</span>
        </button>
      </div>
    </div>
  </div>
</template>
